import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, WaterReflection } from './Sparkles';
const screenshots = [{
  id: 'stats',
  title: 'Weekly Insights',
  description: 'Understand your patterns without judgment',
  image: 'https://cdn.magicpatterns.com/uploads/6uuRCUNEThhQr95pEyF4b2/weekly_insight.png'
}, {
  id: 'dashboard',
  title: 'Daily Dashboard',
  description: 'See your balance score and screen time at a glance',
  image: 'https://cdn.magicpatterns.com/uploads/w8QVYTWrYb3UE85BHDyPUB/web03.png'
}, {
  id: 'garden',
  title: 'Lotus Garden',
  description: 'Watch your garden bloom with each mindful moment',
  image: 'https://cdn.magicpatterns.com/uploads/w5u66v7VijtL3gvzFEjsu4/lotus_garden.png'
}, {
  id: 'focus',
  title: 'Focus Sessions',
  description: 'Block distracting apps with scheduled sessions',
  image: 'https://cdn.magicpatterns.com/uploads/u1tmihvSHotzwZHJxcmenx/focus_sessions.png'
}];
export function ScreenshotShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  return <section className="py-28 px-6 bg-pond-dark-lighter relative overflow-hidden" data-id="element-171">
      {/* Stillness effects */}
      <Sparkles count={8} className="opacity-40" data-id="element-172" />
      <WaterReflection className="top-1/3" data-id="element-173" />
      <WaterReflection className="bottom-1/4" data-id="element-174" />

      {/* Soft ambient glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-175">
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] bg-pond-blue/5 rounded-full blur-[150px]" animate={{
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-176" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-177">
        <motion.div className="text-center mb-20" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} data-id="element-178">
          <h2 className="text-4xl md:text-6xl font-serif text-pond-cream mb-4" data-id="element-179">
            See Pond in Action
          </h2>
          <p className="text-pond-cream-muted text-lg max-w-xl mx-auto" data-id="element-180">
            A thoughtfully designed experience that helps you find balance
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24" data-id="element-181">
          {/* Phone with active screenshot */}
          <motion.div className="flex-shrink-0" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1
        }} data-id="element-182">
            <div className="relative" data-id="element-183">
              {/* Subtle ripples around phone */}
              <Sparkles count={3} className="-inset-20 opacity-30" data-id="element-184" />

              {/* Phone Frame */}
              <div className="relative w-[320px] md:w-[380px] h-[680px] md:h-[800px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[55px] p-2 shadow-2xl" data-id="element-185">
                {/* Screen */}
                <div className="w-full h-full rounded-[48px] overflow-hidden relative bg-black" data-id="element-186">
                  {/* Dynamic Content */}
                  <motion.img key={activeIndex} src={screenshots[activeIndex].image} alt={screenshots[activeIndex].title} className="w-full h-full object-contain" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.5
                }} data-id="element-187" />
                </div>
              </div>

              {/* Soft glow */}
              <motion.div className="absolute inset-0 bg-pond-blue/15 blur-[100px] -z-10 scale-75" animate={{
              opacity: [0.3, 0.5, 0.3]
            }} transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-188" />
            </div>
          </motion.div>

          {/* Screenshot selector */}
          <div className="flex-1 w-full max-w-xl" data-id="element-189">
            <div className="space-y-5" data-id="element-190">
              {screenshots.map((screenshot, index) => <motion.button key={screenshot.id} className={`w-full text-left p-7 rounded-3xl border transition-all duration-500 ${activeIndex === index ? 'bg-pond-dark-card/80 border-pond-blue/30' : 'bg-transparent border-pond-blue/10 hover:border-pond-blue/20'}`} onClick={() => setActiveIndex(index)} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1,
              duration: 0.5
            }} data-id="element-191">
                  <div className="flex items-center gap-5" data-id="element-192">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors duration-500 ${activeIndex === index ? 'bg-pond-blue/20' : 'bg-pond-dark-card/50'}`} data-id="element-193">
                      <span className="text-2xl" data-id="element-194">
                        {index === 0 ? '📈' : index === 1 ? '📊' : index === 2 ? '🪷' : '🎯'}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-195">
                      <h3 className={`text-xl font-medium mb-1 transition-colors duration-500 ${activeIndex === index ? 'text-pond-cream' : 'text-pond-cream-muted'}`} data-id="element-196">
                        {screenshot.title}
                      </h3>
                      <p className="text-pond-cream-muted/70 text-base" data-id="element-197">
                        {screenshot.description}
                      </p>
                    </div>
                    {activeIndex === index && <motion.div className="w-2 h-2 rounded-full bg-pond-blue/60" layoutId="activeIndicator" transition={{
                  duration: 0.5
                }} data-id="element-198" />}
                  </div>
                </motion.button>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}